/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import type { MantineTheme } from '@mantine/core';
import { createTheme, mergeThemeOverrides } from '@mantine/core';

import { colorToken, variablesToken } from '@fai/design-tokens';

import { actionIconTheme } from './component-theme/action-icon';
import { buttonTheme } from './component-theme/button';
import { codeTheme } from './component-theme/code';
import { inputTheme } from './component-theme/inputs/input';
import { menuTheme } from './component-theme/menu';
import { modalTheme } from './component-theme/modal';
import { radioTheme } from './component-theme/radio';
import { segmentedControlTheme } from './component-theme/segmented-control';
import { switchTheme } from './component-theme/switch';
import { tableTheme } from './component-theme/table';
import { tabsTheme } from './component-theme/tabs';
import { textTheme } from './component-theme/text';
import { titleTheme } from './component-theme/title';
import { tooltipTheme } from './component-theme/tooltip';
import { pfTheme } from './pf-theme';

const themeOverride = createTheme({
  fontSmoothing: true,
  fontFamily: 'InterVariable, sans-serif', // Text
  fontFamilyMonospace: 'Roboto Mono, monospace', // Code
  headings: { fontFamily: 'Saans, sans-serif' }, // Title
  black: colorToken.core.color.black,
  white: colorToken.core.color.white,
  primaryColor: 'blue',
  colors: {
    blue: [
      colorToken.core.color.blue['000'],
      colorToken.core.color.blue['100'],
      colorToken.core.color.blue['200'],
      colorToken.core.color.blue['300'],
      colorToken.core.color.blue['400'],
      colorToken.core.color.blue['500'],
      colorToken.core.color.blue['600'],
      colorToken.core.color.blue['700'],
      colorToken.core.color.blue['800'],
      colorToken.core.color.blue['900'],
    ],
    gray: [
      colorToken.core.color.grey['000'],
      colorToken.core.color.grey['100'],
      colorToken.core.color.grey['200'],
      colorToken.core.color.grey['300'],
      colorToken.core.color.grey['400'],
      colorToken.core.color.grey['500'],
      colorToken.core.color.grey['600'],
      colorToken.core.color.grey['700'],
      colorToken.core.color.grey['800'],
      colorToken.core.color.grey['900'],
    ],
    red: [
      colorToken.core.color.red['000'],
      colorToken.core.color.red['100'],
      colorToken.core.color.red['200'],
      colorToken.core.color.red['300'],
      colorToken.core.color.red['400'],
      colorToken.core.color.red['500'],
      colorToken.core.color.red['600'],
      colorToken.core.color.red['700'],
      colorToken.core.color.red['800'],
      colorToken.core.color.red['900'],
    ],
    green: [
      colorToken.core.color.green['000'],
      colorToken.core.color.green['100'],
      colorToken.core.color.green['200'],
      colorToken.core.color.green['300'],
      colorToken.core.color.green['400'],
      colorToken.core.color.green['500'],
      colorToken.core.color.green['600'],
      colorToken.core.color.green['700'],
      colorToken.core.color.green['800'],
      colorToken.core.color.green['900'],
    ],
    yellow: [
      colorToken.core.color.yellow['000'],
      colorToken.core.color.yellow['100'],
      colorToken.core.color.yellow['200'],
      colorToken.core.color.yellow['300'],
      colorToken.core.color.yellow['400'],
      colorToken.core.color.yellow['500'],
      colorToken.core.color.yellow['600'],
      colorToken.core.color.yellow['700'],
      colorToken.core.color.yellow['800'],
      colorToken.core.color.yellow['900'],
    ],
    lime: [
      colorToken.core.color.lime['000'],
      colorToken.core.color.lime['100'],
      colorToken.core.color.lime['200'],
      colorToken.core.color.lime['300'],
      colorToken.core.color.lime['400'],
      colorToken.core.color.lime['500'],
      colorToken.core.color.lime['600'],
      colorToken.core.color.lime['700'],
      colorToken.core.color.lime['800'],
      colorToken.core.color.lime['900'],
    ],
    violet: [
      colorToken.core.color.violet['000'],
      colorToken.core.color.violet['100'],
      colorToken.core.color.violet['200'],
      colorToken.core.color.violet['300'],
      colorToken.core.color.violet['400'],
      colorToken.core.color.violet['500'],
      colorToken.core.color.violet['600'],
      colorToken.core.color.violet['700'],
      colorToken.core.color.violet['800'],
      colorToken.core.color.violet['900'],
    ],
    rose: [
      colorToken.core.color.rose['000'],
      colorToken.core.color.rose['100'],
      colorToken.core.color.rose['200'],
      colorToken.core.color.rose['300'],
      colorToken.core.color.rose['400'],
      colorToken.core.color.rose['500'],
      colorToken.core.color.rose['600'],
      colorToken.core.color.rose['700'],
      colorToken.core.color.rose['800'],
      colorToken.core.color.rose['900'],
    ],
  },
  primaryShade: {
    light: 7,
    dark: 5,
  },
  scale: 1,
  focusRing: 'auto',
  defaultRadius: 'sm',
  radius: {
    xs: 'calc(0.125rem * var(--mantine-scale))',
    sm: `calc(${variablesToken.radius.sm} * var(--mantine-scale))`,
    md: `calc(${variablesToken.radius.md} * var(--mantine-scale))`,
    lg: `calc(${variablesToken.radius.lg} * var(--mantine-scale))`,
    xl: 'calc(2rem * var(--mantine-scale))',
  },
  spacing: {
    xs: 'calc(0.625rem * var(--mantine-scale))',
    sm: 'calc(0.75rem * var(--mantine-scale))',
    md: 'calc(1rem * var(--mantine-scale))',
    lg: 'calc(1.25rem * var(--mantine-scale))',
    xl: 'calc(2rem * var(--mantine-scale))',
  },
});

export const theme = mergeThemeOverrides(
  themeOverride,
  pfTheme,
  actionIconTheme,
  buttonTheme,
  codeTheme,
  inputTheme,
  modalTheme,
  radioTheme,
  switchTheme,
  tableTheme,
  tabsTheme,
  textTheme,
  titleTheme,
  tooltipTheme,
  menuTheme,
  segmentedControlTheme
) as MantineTheme;
